<template>
  <Loader v-if="loader" />
  <div class="card">
    <h3>Andamento CAB</h3>

    <div class="flex mb-2 justify-content-between align-items-center">
      <div>
        <Button
          label="EE"
          icon="pi pi-bolt"
          :class="['p-button-ml', 'mr-2', filtroTipo === 'energy' ? 'p-button-primary' : 'p-button-outlined']"
          @click="onChangeTipo('energy')"
        />
        <Button
          label="Gas"
          icon="pi pi-fire"
          :class="['p-button-ml', 'mr-2', filtroTipo === 'gas' ? 'p-button-primary' : 'p-button-outlined']"
          @click="onChangeTipo('gas')"
        />
        <Button
          label="Tutti"
          icon="pi pi-table"
          :class="['p-button-ml', filtroTipo === 'all' ? 'p-button-primary' : 'p-button-outlined']"
          @click="onChangeTipo('all')"
        />
      </div>
      <div class="flex align-items-center">
        <label class="mr-2">Anno</label>
        <Dropdown
          v-model="selectedAnno"
          :options="anniOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Tutti"
          class="w-8rem"
          @change="onChangeAnno"
          :showClear="true"
        />
      </div>
    </div>

    <div class="card">
      <div class="flex justify-content-between align-items-center mb-3">
        <h4 class="m-0">
          {{ selectedTipo === 'energy' ? 'Energia' : 'Gas' }}
        </h4>
        <Button
          label="Esporta"
          icon="pi pi-download"
          class="p-button-secondary"
          @click="onExport"
        />
      </div>

      <DataTable
        v-if="selectedTipo === 'energy'"
        ref="dtEnergy"
        :value="pivotEnergyItems"
        v-model:filters="filtersEnergy"
        :paginator="true"
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50]"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} a {last} di {totalRecords}"
        responsiveLayout="scroll"
      >
        <template #header>
          <div class="table-header p-d-flex p-flex-column p-flex-md-row p-jc-md-end">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText type="search" v-model="filtersEnergy['global'].value" placeholder="Cerca..." />
            </span>
          </div>
        </template>
        <template #empty>
          <div class="text-center">Nessun dato disponibile.</div>
        </template>

        <Column field="agente" header="Agente" :sortable="true" />
        <Column
          v-for="mese in columnsEnergy"
          :key="mese"
          :field="mese"
          :header="mese"
          :sortable="true"
        >
          <template #body="{ data }">
            <div
              :class="[
                'mese-box',
                { 'mese-box-decreased': isDecreased(data, mese, 'energy') }
              ]"
            >
              {{ data[mese] ?? 0 }}
            </div>
          </template>
        </Column>
      </DataTable>

      <DataTable
        v-else
        ref="dtGas"
        :value="pivotGasItems"
        v-model:filters="filtersGas"
        :paginator="true"
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50]"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} a {last} di {totalRecords}"
        responsiveLayout="scroll"
      >
        <template #header>
          <div class="table-header p-d-flex p-flex-column p-flex-md-row p-jc-md-end">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText type="search" v-model="filtersGas['global'].value" placeholder="Cerca..." />
            </span>
          </div>
        </template>
        <template #empty>
          <div class="text-center">Nessun dato disponibile.</div>
        </template>

        <Column field="agente" header="Agente" :sortable="true" />
        <Column
          v-for="mese in columnsGas"
          :key="mese"
          :field="mese"
          :header="mese"
          :sortable="true"
        >
          <template #body="{ data }">
            <div
              :class="[
                'mese-box',
                { 'mese-box-decreased': isDecreased(data, mese, 'gas') }
              ]"
            >
              {{ data[mese] ?? 0 }}
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from 'vue'
import { FilterMatchMode } from 'primevue/api'
import Loader from '@/components/Loader.vue'
import Service from '@/services/ask-pratiche/pratiche-service'

export default {
  components: {
    Loader,
  },
  setup() {
    const loader = ref(false)
    const energyItems = ref([])
    const gasItems = ref([])
    const pivotEnergyItems = ref([])
    const pivotGasItems = ref([])
    const columnsEnergy = ref([])
    const columnsGas = ref([])
    const selectedTipo = ref('energy')
    const filtroTipo = ref('all')
    const selectedAnno = ref(null)
    const anniOptions = ref([])
    const dtEnergy = ref()
    const dtGas = ref()

    const filtersEnergy = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    })
     
    const filtersGas = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    })

    const self = getCurrentInstance()
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    self.$http = self.appContext.config.globalProperties.$http
    const service = new Service(self.$http, self.$eventBus)

    const initAnniOptions = () => {
      const currentYear = new Date().getFullYear()
      const list = []
      for (let i = 0; i < 6; i++) {
        const y = currentYear - i
        list.push({ label: String(y), value: y })
      }
      anniOptions.value = list
      selectedAnno.value = currentYear
    }

    const buildPivot = (items) => {
      const mesi = Array.from(new Set(items.map((el) => el.mese))).sort()
      const map = new Map()

      items.forEach((el) => {
        const key = el.name
        if (!map.has(key)) {
          map.set(key, { agente: key })
        }
        const row = map.get(key)
        row[el.mese] = el.totale
      })

      return {
        mesi,
        rows: Array.from(map.values()),
      }
    }
       
    const loadData = () => {
      loader.value = true
      service
        .getInfoAndamentoCab(filtroTipo.value, selectedAnno.value)
        .then((res) => {
          energyItems.value = res.items.energy || []
          gasItems.value = res.items.gas || []

          const energyPivot = buildPivot(energyItems.value)
          pivotEnergyItems.value = energyPivot.rows
          columnsEnergy.value = energyPivot.mesi

          const gasPivot = buildPivot(gasItems.value)
          pivotGasItems.value = gasPivot.rows
          columnsGas.value = gasPivot.mesi
        })
        .finally(() => {
          loader.value = false
        })
    }

    const onChangeTipo = (tipo) => {
      if (tipo === 'all') {
        filtroTipo.value = 'all'
      } else {
        selectedTipo.value = tipo
        filtroTipo.value = tipo
      }
      loadData()
    }

    const onChangeAnno = () => {
      loadData()
    }

    const isDecreased = (row, mese, tipo) => {
      const cols = tipo === 'energy' ? columnsEnergy.value : columnsGas.value
      const index = cols.indexOf(mese)
      if (index <= 0) return false

      const prevMese = cols[index - 1]
      const currVal = row[mese] ?? 0
      const prevVal = row[prevMese] ?? 0

      return currVal < prevVal
    }

    const onExport = () => {
      if (filtroTipo.value === 'all') {
        service.downloadInfoAndamentoCab('tutti', selectedAnno.value)
      } else if (selectedTipo.value === 'energy') {
        service.downloadInfoAndamentoCab('ee', selectedAnno.value)
      } else {
        service.downloadInfoAndamentoCab('gas', selectedAnno.value)
      }
    }

    onMounted(() => {
      initAnniOptions()
      loadData()
    })

    return {
      loader,
      energyItems,
      gasItems,
      pivotEnergyItems,
      pivotGasItems,
      columnsEnergy,
      columnsGas,
      selectedTipo,
      filtroTipo,
      selectedAnno,
      anniOptions,
      filtersEnergy,
      filtersGas,
      dtEnergy,
      dtGas,
      onExport,
      onChangeTipo,
      onChangeAnno,
      isDecreased,
    }
  },
}
</script>
<style scoped>
.mese-box {
  display: inline-flex;
  min-width: 3rem;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: var(--surface-b);
}

.mese-box-decreased {
  background-color: #fca5a5;
  color: #7f1d1d;
  font-weight: 600;
}
</style>

